import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useDeliverable} from "../../models/deliverable/use-deliverable";
import {useGoal} from "../../models/goal/use-goal";
import {useProject} from "../../models/project/use-project";
import {useTask} from "../../models/task/use-task";
import {
    setWorkspaceGraphProjects,
    upsertWorkspaceGoal,
    upsertWorkspaceProject,
    upsertWorkspaceTask,
    workspaceGraphSelectors,
} from "../workspace-graph-slice";
import {setWorkspaceMembership} from "../app-slice";
import GoalSidebar from "../../pages/goals/components/goal-sidebar";
import DeliverableSidebar from "../../pages/projects/components/deliverable-sidebar";
import ProjectSidebar from "../../pages/projects/components/project-sidebar";
import TaskSidebar from "../../pages/projects/components/task-sidebar";
import {entityKey, sameEntity} from "../../pages/inbox/inbox-utils";
import GoalUpdateViewDialog from "../../pages/goals/components/goal-update-view-dialog";
import GoalPeriodReviewDialog from "../../pages/goals/components/goal-period-review-dialog";
import ProjectUpdateViewDialog from "../../pages/projects/components/project-update-view-dialog";

// Opens the detail sidebar of a task, goal, project or milestone on top of the current page. Shared by the Activity
// tab and by notification links (?task= / ?goal= / ?project= / ?deliverable= on any route). Goal updates open their view
// modal (?goalUpdate=), project updates theirs (?projectUpdate=) and the end-of-period review its modal
// (?goalPeriodReview=1).

export function findReferencedItem(items, reference) {
    return (items || []).find((item) => sameEntity(item, reference));
}

export function workspaceDeliverables(projects) {
    return (projects || []).flatMap((project) => project?.deliverables || []);
}

export default function useEntitySidebars() {
    const dispatch = useDispatch();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const tasks = useSelector(workspaceGraphSelectors.tasks);
    const projects = useSelector(workspaceGraphSelectors.projects);
    const goals = useSelector(workspaceGraphSelectors.goals);
    const taskApi = useTask();
    const goalApi = useGoal();
    const projectApi = useProject();
    const deliverableApi = useDeliverable();
    const [selectedTask, setSelectedTask] = useState();
    const [selectedGoal, setSelectedGoal] = useState();
    const [selectedProject, setSelectedProject] = useState();
    const [selectedDeliverable, setSelectedDeliverable] = useState();
    const [goalUpdateId, setGoalUpdateId] = useState(null);
    const [periodReviewVisible, setPeriodReviewVisible] = useState(false);
    const [projectUpdateId, setProjectUpdateId] = useState(null);

    const close = () => {
        setSelectedTask(undefined);
        setSelectedGoal(undefined);
        setSelectedProject(undefined);
        setSelectedDeliverable(undefined);
        setGoalUpdateId(null);
        setPeriodReviewVisible(false);
        setProjectUpdateId(null);
    };

    const refreshWorkspaceProjects = async () => {
        const workspaceId = workspaceMembershipData?.workspace?.id;
        if (!workspaceId) return [];
        const refreshedProjects = await projectApi.getProjects(workspaceId);
        if (!Array.isArray(refreshedProjects)) return [];
        dispatch(setWorkspaceGraphProjects(refreshedProjects));
        dispatch(setWorkspaceMembership({
            ...workspaceMembershipData,
            workspace: {...workspaceMembershipData.workspace, projects: refreshedProjects},
        }));
        return refreshedProjects;
    };

    // returns true when a sidebar was opened
    const open = async (reference) => {
        const key = entityKey(reference);
        close();
        if (!reference?.type || !key) return false;
        if (reference.type === "goalUpdate") {
            setGoalUpdateId(key);
            return true;
        }
        if (reference.type === "projectUpdate") {
            setProjectUpdateId(key);
            return true;
        }
        if (reference.type === "goalPeriodReview") {
            setPeriodReviewVisible(true);
            return true;
        }
        if (reference.type === "task") {
            const task = findReferencedItem(tasks, reference) || await taskApi.getTask(key);
            if (!task) return false;
            dispatch(upsertWorkspaceTask(task));
            setSelectedTask(task);
            return true;
        }
        if (reference.type === "goal") {
            const goal = findReferencedItem(goals, reference) || await goalApi.getGoal(key);
            if (!goal) return false;
            dispatch(upsertWorkspaceGoal(goal));
            setSelectedGoal(goal);
            return true;
        }
        if (reference.type === "project") {
            const project = findReferencedItem(projects, reference) || await projectApi.getProject(key);
            if (!project) return false;
            dispatch(upsertWorkspaceProject(project));
            setSelectedProject(project);
            return true;
        }
        if (reference.type === "deliverable") {
            const deliverable = findReferencedItem(workspaceDeliverables(projects), reference)
                || await deliverableApi.getDeliverable(key);
            if (!deliverable) return false;
            setSelectedDeliverable(deliverable);
            return true;
        }
        return false;
    };

    const updateTask = async (taskData, updateData, subTasks = []) => {
        let updatedTask = null;
        if (updateData) {
            updatedTask = await taskApi.updateTask(entityKey(taskData), updateData);
            if (updatedTask) {
                dispatch(upsertWorkspaceTask(updatedTask));
                setSelectedTask((current) => (sameEntity(current, taskData) ? updatedTask : current));
            }
        }
        await Promise.all((subTasks || []).map(async (subTask) => {
            const subTaskData = {...(subTask || {})};
            const documentId = subTaskData.documentId;
            delete subTaskData.id;
            delete subTaskData.documentId;
            delete subTaskData.createdAt;
            delete subTaskData.updatedAt;
            const dueDate = subTaskData.dueDate
                ? Number.isInteger(subTaskData.dueDate)
                    ? new Date(subTaskData.dueDate * 1000).toISOString()
                    : subTaskData.dueDate
                : Date.now();
            const item = documentId
                ? await taskApi.updateTask(documentId, {...subTaskData, dueDate})
                : await taskApi.createTask({
                    ...subTaskData,
                    parentTask: taskData?.id,
                    dueDate,
                    workspace: workspaceMembershipData?.workspace?.id,
                });
            if (item) dispatch(upsertWorkspaceTask(item));
            return item;
        }));
        return updatedTask;
    };

    const updateProject = async (projectData, updateData) => {
        const updatedProject = await projectApi.updateProject(entityKey(projectData), updateData);
        if (updatedProject) {
            dispatch(upsertWorkspaceProject(updatedProject));
            setSelectedProject((current) => (sameEntity(current, projectData) ? updatedProject : current));
        }
        return updatedProject;
    };

    const updateDeliverable = async (updateData) => {
        if (!selectedDeliverable) return null;
        const updatedDeliverable = await deliverableApi.updateDeliverable(entityKey(selectedDeliverable), updateData);
        if (!updatedDeliverable) return null;
        setSelectedDeliverable(updatedDeliverable);
        const refreshed = findReferencedItem(workspaceDeliverables(await refreshWorkspaceProjects()), updatedDeliverable);
        if (refreshed) setSelectedDeliverable(refreshed);
        return updatedDeliverable;
    };

    const sidebars = (
        <>
            <TaskSidebar selectedTask={selectedTask} setSelectedTask={setSelectedTask} updateTask={updateTask}/>
            <GoalSidebar goalData={selectedGoal} setGoalData={setSelectedGoal}/>
            <ProjectSidebar projectData={selectedProject} setProjectData={setSelectedProject} updateProject={updateProject}/>
            <DeliverableSidebar
                selectedDeliverable={selectedDeliverable}
                setSelectedDeliverable={setSelectedDeliverable}
                tasks={tasks || []}
                updateTask={updateTask}
                onSubmit={updateDeliverable}
            />
            <GoalUpdateViewDialog updateId={goalUpdateId} visible={Boolean(goalUpdateId)} onHide={() => setGoalUpdateId(null)}/>
            <GoalPeriodReviewDialog visible={periodReviewVisible} onHide={() => setPeriodReviewVisible(false)}/>
            <ProjectUpdateViewDialog updateId={projectUpdateId} visible={Boolean(projectUpdateId)} onHide={() => setProjectUpdateId(null)}/>
        </>
    );

    return {open, close, sidebars, isOpen: Boolean(selectedTask || selectedGoal || selectedProject || selectedDeliverable || goalUpdateId || periodReviewVisible || projectUpdateId)};
}
